import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import { CameraIntegration } from '@sb/integrations/types/cameraTypes';

import {
  Detection3DPoseEstimationConfig,
  LocateMethod,
  ResultOrdering,
} from '../../vision/LocateTypes';
import {
  ProjectionMethodEnum,
  projectionMethodNeedsDepth,
} from '../../vision/visionConfigSchemas';
import { RegionOfInterest } from '../../vision/VisionInterface';
import { CaptureConfig } from '../Capture/captureConfig';

export { ProjectionMethodEnum, projectionMethodNeedsDepth };

// The step-argument form of the discriminator: optional on input, so an
// argument set written before it existed still parses, and defaulted to the
// projection those callers meant.
export const ProjectionMethod =
  ProjectionMethodEnum.optional().default('ManualPlane');

export type ProjectionMethod = zod.infer<typeof ProjectionMethodEnum>;

// Match defaults from python/libs/depth/detected_plane.py
export const DEFAULT_RANSAC_MAX_ITERATIONS = 1000;
export const DEFAULT_RANSAC_DISTANCE_THRESHOLD = 0.005;
export const DEFAULT_RANSAC_MIN_SAMPLES = 3;

export const RANSAC_QUALITY_LEVELS = {
  Fast: 100,
  Balanced: 500,
  Accurate: 1000,
} as const;

export type RansacQuality = keyof typeof RANSAC_QUALITY_LEVELS;

export const RansacParams = zod.object({
  maxIterations: zod.number().optional().default(DEFAULT_RANSAC_MAX_ITERATIONS),
  distanceThreshold: zod
    .number()
    .optional()
    .default(DEFAULT_RANSAC_DISTANCE_THRESHOLD),
  minSamples: zod.number().optional().default(DEFAULT_RANSAC_MIN_SAMPLES),
});

// Tilt angle threshold: reject detections where the surface normal
// deviates from vertical by more than this angle (degrees).
// Min 1° (0 is reserved as a "disabled" sentinel on the backend), 90 = accept all.
export const DEFAULT_MAX_TILT_ANGLE = 15;

export default zod.object({
  camera: CameraIntegration.optional(),
  // Optional equipment ID for camera-bot-cpp mode (when set, camera field is ignored)
  cameraEquipmentId: zod.string().optional(),
  regionOfInterest: RegionOfInterest,
  method: LocateMethod,
  positionListID: zod.string(), // The position list to output results to
  filters: zod.object({
    resultsLimit: zod.optional(zod.number()), // The max number of results to output
  }),
  resultOrdering: ResultOrdering.optional(),
  transform: CartesianPose,
  planeID: zod.string().optional(), // Required for ManualPlane, unused for the depth methods
  objectHeight: zod.number(), // in meters
  projectionMethod: ProjectionMethod,
  ransacParams: RansacParams.optional(),
  maxTiltAngle: zod
    .number()
    .min(1)
    .max(90)
    .optional()
    .default(DEFAULT_MAX_TILT_ANGLE),
  // DetectedPlane only. Reject a surface fit below this RANSAC inlier ratio:
  // RANSAC always returns *a* plane, so without a floor a meaningless fit is
  // indistinguishable from a clean one downstream. Omitted/0 disables the check.
  minInlierRatio: zod.number().min(0).max(1).optional(),
  // DetectedPlane only. When the fit is rejected or fails, emit a top-down
  // grasp (median depth over the support + world axes) instead of dropping the
  // detection. The pose is then a guess, not a measurement — vision-bot flags
  // it back via `usedTopDownFallback`.
  fallbackToTopDown: zod.boolean().optional(),
  disablePlaneOrientation: zod.boolean().optional().default(false),
  detection3dConfig: Detection3DPoseEstimationConfig.optional(),
  captureConfig: CaptureConfig.optional(),
});
